import {
  TOKEN_KEY,
  type AccountActivityItem,
  type AccountSummary,
  type FinanceTx,
  type Position,
  type TradeHistoryItem,
  type TradingAccount,
} from './types'
import { apiUrl } from '../lib/api-base'
import { getAuthItem } from '../shared/authStorage'

export type ClientDeposit = {
  id: number
  reference?: string
  amount?: string | number
  currency?: string
  status?: string
  provider?: string
  created_at?: string
  reviewed_at?: string
  completed_at?: string
  trading_account_id?: number
  mt5_login_masked?: string
  asset?: string
  network?: string
  txid_masked?: string
  admin_note?: string
  rejection_reason?: string
  is_demo?: boolean
  payment_address?: string
  payment_url?: string
  qr_url?: string
  expires_at?: string
  invoice_expires_at?: string
  requested_fiat_amount?: string | number
  requested_fiat_currency?: string
  quoted_crypto_amount?: string | number
  quoted_crypto_currency?: string
  provider_status?: string
  has_screenshot?: boolean
}

export type ClientWithdrawal = {
  id: number
  reference?: string
  tradingAccountId?: number
  amount?: string | number
  currency?: string
  destination?: string
  walletAddress?: string
  note?: string
  status?: string
  rejectionReason?: string
  submittedAt?: string
  reviewedAt?: string
  completedAt?: string
  expectedReview?: string
}

type ApiError = { code?: string; message?: string }

export class ClientApiError extends Error {
  code: string
  status: number

  constructor(message: string, code = 'REQUEST_FAILED', status = 0) {
    super(message)
    this.name = 'ClientApiError'
    this.code = code
    this.status = status
  }
}

export function isDemoActivationRequired(err: unknown): boolean {
  return (
    err instanceof ClientApiError &&
    (err.code === 'DEMO_ACTIVATION_REQUIRED' || err.code === 'ACCOUNT_CREATION_DISABLED')
  )
}

export function isLiveActivationRequired(err: unknown): boolean {
  return (
    err instanceof ClientApiError &&
    (err.code === 'LIVE_ACCOUNT_CREATION_DISABLED' ||
      err.code === 'LIVE_ACCOUNT_CREATION_UNAVAILABLE' ||
      err.code === 'ACCOUNT_CREATION_DISABLED')
  )
}

export function isLiveAlreadyExists(err: unknown): boolean {
  return err instanceof ClientApiError && err.code === 'LIVE_ACCOUNT_ALREADY_EXISTS'
}

export const DEMO_ACTIVATION_REQUEST_NOTICE =
  'Your request has been received. Our operations team will activate your Demo trading account.'


async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAuthItem(TOKEN_KEY)
  const headers = new Headers(init.headers || {})
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(apiUrl(path), { ...init, headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = (data as { error?: ApiError }).error
    throw new ClientApiError(
      err?.message || `Request failed (${res.status})`,
      err?.code || 'REQUEST_FAILED',
      res.status,
    )
  }
  return data as T
}

/** Client-side Demo MT5 password for create contract. Never logged or returned. */
function generateTradingPassword(length = 16): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const digits = '23456789'
  const symbols = '!@#$%^&*-_=+'
  const all = upper + lower + digits + symbols
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  const chars = Array.from(bytes, (b) => all[b % all.length])
  chars[0] = upper[bytes[0] % upper.length]
  chars[1] = lower[bytes[1] % lower.length]
  chars[2] = digits[bytes[2] % digits.length]
  chars[3] = symbols[bytes[3] % symbols.length]
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = bytes[i] % (i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

export type RegisterPayload = {
  email: string
  password: string
  first_name: string
  last_name: string
  phone?: string
  country?: string
  ib_code?: string
}

export type RegisterResponse = {
  success: boolean
  clientId: number
  email: string
  emailVerified: boolean
  verificationRequired: boolean
  /** Present only when SMTP is unavailable (local/dev). Never rely on this in production UI alone. */
  devVerificationToken?: string
}

export const api = {
  login(email: string, password: string) {
    return request<{ accessToken: string; client?: Record<string, unknown> }>(
      '/api/client/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    )
  },
  forgotPassword(email: string) {
    return request<{ success: boolean; message?: string; devResetToken?: string }>(
      '/api/client/forgot-password',
      { method: 'POST', body: JSON.stringify({ email }) },
    )
  },
  resetPassword(payload: {
    token: string
    newPassword: string
    confirmPassword: string
  }) {
    return request<{ success: boolean; message?: string }>('/api/client/reset-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  googleAuthConfig() {
    return request<{ enabled: boolean; clientId: string }>('/api/client/auth/google/config')
  },
  loginWithGoogle(credential: string, ibCode = '') {
    return request<{ accessToken: string; client?: Record<string, unknown> }>(
      '/api/client/auth/google',
      {
        method: 'POST',
        body: JSON.stringify({
          credential,
          ib_code: ibCode || '',
        }),
      },
    )
  },
  register(payload: RegisterPayload) {
    return request<RegisterResponse>('/api/client/register', {
      method: 'POST',
      body: JSON.stringify({
        email: payload.email,
        password: payload.password,
        first_name: payload.first_name,
        last_name: payload.last_name,
        phone: payload.phone || '',
        country: payload.country || '',
        ib_code: payload.ib_code || '',
      }),
    })
  },
  verifyEmail(token: string) {
    return request<{
      success: boolean
      emailVerified: boolean
      accessToken?: string
      client?: Record<string, unknown>
    }>('/api/client/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },
  changePortalPassword(payload: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }) {
    return request<{ success: boolean; message?: string; otherSessionsRevoked?: number }>(
      '/api/client/change-password',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
  },
  changeTradingPassword(
    accountId: number,
    payload: {
      newPassword: string
      confirmPassword: string
      currentPassword?: string
    },
  ) {
    return request<{ success: boolean; message?: string; accountId?: number; mode?: string }>(
      `/api/client/trading-accounts/${accountId}/password`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': `web-mt5-pw-${accountId}-${Date.now()}` },
        body: JSON.stringify({
          newPassword: payload.newPassword,
          confirmPassword: payload.confirmPassword,
          currentPassword: payload.currentPassword || undefined,
        }),
      },
    )
  },
  listAccounts() {
    return request<{ success: boolean; accounts: TradingAccount[] }>(
      '/api/client/trading-accounts',
    )
  },
  createAccount(idempotencyKey: string) {
    // Demo: password is generated for the request (practice account).
    const tradingPassword = generateTradingPassword(16)
    return request<{ success: boolean; account: TradingAccount }>(
      '/api/client/trading-accounts',
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({
          mode: 'DEMO',
          tradingPassword,
          confirmTradingPassword: tradingPassword,
        }),
      },
    )
  },
  createLiveAccount(
    idempotencyKey: string,
    tradingPassword: string,
    confirmTradingPassword: string,
  ) {
    return request<{ success: boolean; account: TradingAccount; message?: string }>(
      '/api/client/trading-accounts',
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({
          mode: 'LIVE',
          tradingPassword,
          confirmTradingPassword,
        }),
      },
    )
  },
  summary(accountId: number) {
    return request<{ success: boolean; account: AccountSummary }>(
      `/api/client/trading-accounts/${accountId}/summary`,
    )
  },
  openPositions(accountId: number) {
    return request<{ success: boolean; positions: Position[]; count: number }>(
      `/api/client/trading-accounts/${accountId}/open-positions`,
    )
  },
  pendingOrders(accountId: number) {
    return request<{ success: boolean; orders: unknown[]; count: number }>(
      `/api/client/trading-accounts/${accountId}/pending-orders`,
    )
  },
  tradeHistory(
    accountId: number,
    params: {
      from?: string
      to?: string
      symbol?: string
      side?: string
      page?: number
      pageSize?: number
    } = {},
  ) {
    const q = new URLSearchParams()
    if (params.from) q.set('from', params.from)
    if (params.to) q.set('to', params.to)
    if (params.symbol) q.set('symbol', params.symbol)
    if (params.side) q.set('side', params.side)
    if (params.page) q.set('page', String(params.page))
    if (params.pageSize) q.set('pageSize', String(params.pageSize))
    const qs = q.toString()
    return request<{
      success: boolean
      trades?: TradeHistoryItem[]
      deals?: TradeHistoryItem[]
      sync_state?: string
      last_sync_at?: string
      pagination?: {
        page: number
        page_size: number
        total: number
        total_pages: number
        from?: string
        to?: string
      }
    }>(`/api/client/trading-accounts/${accountId}/trade-history${qs ? `?${qs}` : ''}`)
  },
  accountActivity(
    accountId: number,
    params: { from?: string; to?: string; page?: number; pageSize?: number } = {},
  ) {
    const q = new URLSearchParams()
    if (params.from) q.set('from', params.from)
    if (params.to) q.set('to', params.to)
    if (params.page) q.set('page', String(params.page))
    if (params.pageSize) q.set('pageSize', String(params.pageSize))
    const qs = q.toString()
    return request<{
      success: boolean
      activities?: AccountActivityItem[]
      sync_state?: string
      last_sync_at?: string
      pagination?: {
        page: number
        page_size: number
        total: number
        total_pages: number
      }
    }>(`/api/client/trading-accounts/${accountId}/account-activity${qs ? `?${qs}` : ''}`)
  },
  transactions(params: { page?: number; pageSize?: number } = {}) {
    const q = new URLSearchParams()
    if (params.page) q.set('page', String(params.page))
    if (params.pageSize) q.set('pageSize', String(params.pageSize))
    const qs = q.toString()
    return request<{
      success: boolean
      transactions: FinanceTx[]
      pagination?: {
        page: number
        page_size: number
        total: number
        total_pages: number
      }
    }>(`/api/client/transactions${qs ? `?${qs}` : ''}`)
  },
  listDeposits() {
    return request<{ success: boolean; deposits: ClientDeposit[]; pagination?: Record<string, number> }>(
      '/api/client/deposits',
    )
  },
  getDeposit(depositId: number) {
    return request<{
      success: boolean
      deposit: ClientDeposit & Record<string, unknown>
      timeline?: Array<{ event_type?: string; timestamp?: string; description?: string }>
      warning?: string
    }>(`/api/client/deposits/${depositId}`)
  },
  depositConfig() {
    return request<{
      success: boolean
      enabled?: boolean
      providers?: Array<{
        provider: string
        enabled?: boolean
        label?: string
        asset?: string
        network?: string
        wallet_address?: string
        minimum_usd?: string | number
        maximum_usd?: string | number
        warning?: string
        automatic_confirmation?: boolean
      }>
      uniwire_enabled?: boolean
      min_amount?: string | number
      max_amount?: string | number
      currency?: string
    }>('/api/client/deposit-config')
  },
  createManualDeposit(payload: {
    tradingAccountId: number
    amount: string
    txid: string
    note?: string
  }) {
    return request<{ success: boolean; deposit: ClientDeposit }>('/api/client/deposits', {
      method: 'POST',
      body: JSON.stringify({
        tradingAccountId: payload.tradingAccountId,
        amount: payload.amount,
        txid: payload.txid,
        note: payload.note || undefined,
        asset: 'USDT',
        network: 'TRC20',
      }),
    })
  },
  createManualDepositMultipart(form: FormData) {
    return request<{ success: boolean; deposit: ClientDeposit }>('/api/client/deposits', {
      method: 'POST',
      body: form,
    })
  },
  createUniwireDeposit(payload: {
    tradingAccountId: number
    amountUsd: string
    asset?: string
    network?: string
  }) {
    return request<{ success: boolean; deposit: ClientDeposit & Record<string, unknown> }>(
      '/api/client/deposits/uniwire',
      {
        method: 'POST',
        headers: { 'Idempotency-Key': `web-uniwire-${Date.now()}` },
        body: JSON.stringify({
          tradingAccountId: payload.tradingAccountId,
          amountUsd: payload.amountUsd,
          asset: payload.asset || 'USDT',
          network: payload.network || 'TRC20',
        }),
      },
    )
  },
  refreshUniwireDeposit(depositId: number) {
    return request<{ success: boolean; deposit?: ClientDeposit & Record<string, unknown> }>(
      `/api/client/deposits/${depositId}/refresh`,
      { method: 'POST' },
    )
  },
  listWithdrawals() {
    return request<{ success: boolean; withdrawals: ClientWithdrawal[] }>(
      '/api/client/withdrawals',
    )
  },
  getWithdrawal(withdrawalId: number) {
    return request<{
      success: boolean
      withdrawal: ClientWithdrawal
      timeline?: Array<{ event_type?: string; timestamp?: string; description?: string }>
      warning?: string
    }>(`/api/client/withdrawals/${withdrawalId}`)
  },
  createWithdrawal(payload: {
    tradingAccountId: number
    amount: string
    destination: string
    walletAddress: string
    note?: string
    availableBalance?: string
  }) {
    const idem =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `web-wd-${crypto.randomUUID()}`
        : `web-wd-${Date.now()}-${Math.random().toString(36).slice(2)}`
    return request<{ success: boolean; withdrawal: ClientWithdrawal }>('/api/client/withdrawals', {
      method: 'POST',
      headers: { 'Idempotency-Key': idem },
      body: JSON.stringify({
        tradingAccountId: payload.tradingAccountId,
        amount: payload.amount,
        destination: payload.destination,
        walletAddress: payload.walletAddress,
        note: payload.note || undefined,
        availableBalance: payload.availableBalance,
      }),
    })
  },

  notifications(limit = 30) {
    return request<{
      success: boolean
      unreadCount: number
      notifications: Array<{
        id: number
        title: string
        body: string
        ctaLabel?: string
        ctaPath?: string
        read: boolean
        createdAt?: string
      }>
    }>(`/api/client/notifications?limit=${limit}`)
  },

  notificationsRead(id?: number) {
    return request<{ success: boolean; updated: number; unreadCount: number }>(
      '/api/client/notifications/read',
      {
        method: 'POST',
        body: JSON.stringify(id != null ? { id } : {}),
      },
    )
  },
}
